import { cn } from '@/utils/tailwind';

import { ExpandableTraceInformation } from '../ExpandableTraceInformation';
import {
  CREATE_DATA_URL_PREFIX_FROM_MIME_TYPE,
  DATA_PREFIX,
  MIME_TYPES,
  MimeType,
} from './constants';

const titleMap = {
  [MIME_TYPES.PDF]: 'PDF Content',
  [MIME_TYPES.JSON]: 'JSON Content',
  [MIME_TYPES.TEXT]: 'Text Content',
  [MIME_TYPES.CSV]: 'CSV Content',
};

// Note: Do not allow text/html or other risky mime types.
// SVG is handled above in the IMAGE case.
const iframeTypes: MimeType[] = [
  MIME_TYPES.PDF,
  MIME_TYPES.JSON,
  MIME_TYPES.TEXT,
  MIME_TYPES.CSV,
];

export function MultimodalContent({
  contentType,
  url,
  contentName,
  unstyled,
  mimeSubtype,
  className,
}: {
  contentType: string | null;
  url: string | undefined;
  contentName: string;
  mimeSubtype?: string;
  unstyled?: boolean;
  className?: string;
}) {
  if (!url || !contentType) return null;

  const contentTypeWithSubtype =
    mimeSubtype && !contentType.includes('/')
      ? `${contentType}/${mimeSubtype}`
      : contentType;

  const isBase64 =
    url.startsWith(DATA_PREFIX) ||
    // if the url is too long, the regex will cause the page to error so assume it's base64
    (url.length > 5_000_000 ? true : /^[A-Za-z0-9+/=]+$/.test(url));
  const srcUrl =
    isBase64 && !url.startsWith(DATA_PREFIX)
      ? `${CREATE_DATA_URL_PREFIX_FROM_MIME_TYPE(contentTypeWithSubtype)}${url}`
      : url;

  if (contentTypeWithSubtype.startsWith(MIME_TYPES.IMAGE)) {
    return (
      <img
        src={srcUrl}
        alt={contentName}
        className={cn('py-1', className)}
        style={{ maxWidth: '100%' }}
      />
    );
  }

  if (contentTypeWithSubtype.startsWith(MIME_TYPES.AUDIO)) {
    return (
      <audio controls className={cn('py-1', className)}>
        <source src={srcUrl} type={contentTypeWithSubtype} />
        Your browser does not support the audio element.
      </audio>
    );
  }

  if (contentTypeWithSubtype.startsWith(MIME_TYPES.VIDEO)) {
    return (
      <video
        className={cn('py-1', className)}
        controls
        style={{ maxWidth: '100%' }}
      >
        <source src={srcUrl} type={contentType} />
        Your browser does not support the video tag.
      </video>
    );
  }

  if (iframeTypes.includes(contentType as MimeType)) {
    // if srcUrl is url-like (https: or data: base64 encoded) then render as iframe
    if (
      isBase64 ||
      srcUrl.startsWith('http://') ||
      srcUrl.startsWith('https://') ||
      (srcUrl.startsWith('/') && !srcUrl.startsWith('/.'))
    ) {
      return (
        <iframe
          className={cn('py-1', className)}
          src={srcUrl}
          title={contentName ?? titleMap[contentType]}
          style={unstyled ? {} : { width: '90vw', height: '80vh' }}
        />
      );
    } else {
      return (
        <div
          className={cn(
            'rounded-md border border-warning p-2 text-xs text-warning',
            className
          )}
        >
          Content with content type {contentTypeWithSubtype} must be either
          start with https:// or be a base64 encoded string.
        </div>
      );
    }
  }

  if (contentTypeWithSubtype.startsWith(MIME_TYPES.TEXT)) {
    // If we have raw text content (not a URL), render it directly
    return (
      <ExpandableTraceInformation
        content={url}
        header={`${contentName} (${contentTypeWithSubtype})`}
        className="px-0"
      />
    );
  }

  return (
    <div
      className={cn(
        'rounded-md border border-warning p-2 text-xs text-warning',
        className
      )}
    >
      Unsupported content type: {contentTypeWithSubtype}
    </div>
  );
}
